import React, { lazy } from 'react'
import { Router, Redirect, Route, Switch } from 'react-router-dom'
import { init as initApm } from '@elastic/apm-rum'
import { ResetCSS } from 'vvs-uikit'
import BigNumber from 'bignumber.js'
import useEagerConnect from 'hooks/useEagerConnect'
import { DatePickerPortal } from 'components/DatePicker'
import { isBlindMode, isSmartRemovalEnabled } from 'utils'
import xVvs from 'views/xVvs'
import { Path } from 'config/constants/paths'
import { isVvsMinerMolePhase2, isVvsMinerMolePhase3, isVvsMinerMolePhase4BlindBoxReveal } from 'config/constants/env'
import { VVS_GOTCHI_MAINTENANCE_MODE } from 'config/constants/vvsgotchi'
import GlobalStyle from './style/Global'
import RootProviders from './Providers'
import ListsUpdater from './state/lists/updater'
import TransactionUpdater from './state/multicall/updater'
import MulticallUpdater from './state/transactions/updater'
import LayoutWithMenu from './Layout'
import SuspenseWithChunkError from './components/SuspenseWithChunkError'
import { ToastListener } from './contexts/ToastsContext'
import PageLoader from './components/Loader/PageLoader'
import ScrollToTop from './components/ScrollToTop'
import history from './routerHistory'
// Views included in the main bundle
import Pools from './views/Pools'
import Swap from './views/Swap'
import LimitOrders from './views/LimitOrders'
import FlashLp from './views/FlashLp'
import Dashboard from './views/Dashboard'
import {
  RedirectDuplicateTokenIds,
  RedirectOldAddLiquidityPathStructure,
  RedirectToAddLiquidity,
} from './views/AddLiquidity/redirects'
import RedirectOldRemoveLiquidityPathStructure from './views/RemoveLiquidity/redirects'
import { RedirectPathToSwapOnly, RedirectToSwap } from './views/Swap/redirects'
import { start as startBugsnag } from './lib/bugsnag'

// Route-based code splitting
// Only pool is included in the main bundle because of it's the most visited page
const Home = lazy(() => import('./views/Home'))
const Farms = lazy(() => import('./views/Farms'))
const Igo = lazy(() => import('./views/Igos'))
const NotFound = lazy(() => import('./views/NotFound'))
const AddLiquidity = lazy(() => import('./views/AddLiquidity'))
const Liquidity = lazy(() => import('./views/Pool'))
const PoolFinder = lazy(() => import('./views/PoolFinder'))
const RemoveLiquidity = lazy(() => import('./views/RemoveLiquidity'))
const Info = lazy(() => import('./views/Info'))
const TradingBattle = lazy(() => import('./views/TradingBattle'))
const VvsMinerMole = lazy(() => import('./views/VvsMinerMole'))
const VvsMinerMoleMyMoles = lazy(() => import('./views/VvsMinerMoleMyMoles'))
const VvsMinerMoleMint = lazy(() => import('./views/VvsMinerMoleMint'))
const VvsMinerMoleGallery = lazy(() => import('./views/VvsMinerMoleGallery'))
const VvsGotchi = lazy(() => import('views/VvsGotchi'))
const VvsGotchiMolePerformance = lazy(() => import('views/VvsGotchiMolePerformance'))

const BoostCalculator = lazy(() => import('./views/BoostCalculator'))

// This config is required for number formatting
BigNumber.config({
  EXPONENTIAL_AT: 1000,
  DECIMAL_PLACES: 80,
})

if (process.env.REACT_APP_ELASTIC_APM_ACTIVE === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const apm = initApm({
    // Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
    serviceName: process.env.REACT_APP_ELASTIC_APM_SERVICE_NAME,
    // Set custom APM Server URL (default: http://localhost:8200)
    serverUrl: process.env.REACT_APP_ELASTIC_APM_SERVER_URL,
    logLevel: 'debug',
    // Set service version (required for sourcemap feature)
    serviceVersion: process.env.REACT_APP_APP_VERSION,
  })
}

const ErrorBoundary = startBugsnag()
const App: React.FC = () => {
  useEagerConnect()

  return (
    <ErrorBoundary>
      <Router history={history}>
        <ScrollToTop>
          <ResetCSS />
          <GlobalStyle />
          <LayoutWithMenu>
            <SuspenseWithChunkError fallback={<PageLoader />}>
              <Switch>
                <Route path="/" exact>
                  <Home />
                </Route>
                <Route path="/farms">
                  <Farms />
                </Route>
                <Route path="/mines">
                  <Pools />
                </Route>
                <Route path="/dashboard">
                  <Dashboard />
                </Route>
                <Route path={Path.IGO}>
                  <Igo />
                </Route>
                {/* Info pages */}
                {!isBlindMode() && (
                  <Route path={['/info', Path.ANALYTICS_XVVS]}>
                    <Info />
                  </Route>
                )}
                <Route path={Path.VVS_MINER_MOLE} exact>
                  <VvsMinerMole />
                </Route>
                {!VVS_GOTCHI_MAINTENANCE_MODE && (
                  <Route path={Path.VVS_GOTCHI}>
                    <VvsGotchi />
                  </Route>
                )}
                <Route path={Path.VVS_GOTCHI_MOLE_PERFORMANCE}>
                  <VvsGotchiMolePerformance />
                </Route>

                {isVvsMinerMolePhase2 && (
                  <Route path={Path.VVS_MINER_MOLE_MY_MOLES} exact>
                    <VvsMinerMoleMyMoles />
                  </Route>
                )}

                {isVvsMinerMolePhase2 && !isVvsMinerMolePhase3 && (
                  <Route path={Path.VVS_MINER_MOLE_MINT}>
                    <VvsMinerMoleMint />
                  </Route>
                )}

                {isVvsMinerMolePhase4BlindBoxReveal && (
                  <Route path={Path.VVS_MINER_MOLE_GALLERY}>
                    <VvsMinerMoleGallery />
                  </Route>
                )}

                <Route exact strict path={Path.BOOST_CALCULATOR}>
                  <BoostCalculator />
                </Route>

                {/* Using this format because these components use routes injected props. We need to rework them with hooks */}
                <Route exact strict path={Path.TRADE_SWAP} component={Swap} />
                <Route exact strict path="/limit-orders" component={LimitOrders} />
                <Route exact strict path="/swap/:outputCurrency" component={RedirectToSwap} />
                <Route exact strict path="/send" component={RedirectPathToSwapOnly} />
                <Route exact strict path="/find" component={PoolFinder} />
                <Route exact strict path={Path.LIQUIDITY} component={Liquidity} />
                <Route exact strict path="/liquidity/orders" component={Liquidity} />
                <Route exact strict path="/liquidity/history" component={Liquidity} />
                <Route exact strict path="/create" component={RedirectToAddLiquidity} />
                <Route exact path="/add" component={AddLiquidity} />
                <Route exact path="/add/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
                <Route exact path="/add/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
                <Route exact path="/create" component={AddLiquidity} />
                <Route exact path="/create/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
                <Route exact path="/create/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
                <Route exact strict path="/remove/:tokens" component={RedirectOldRemoveLiquidityPathStructure} />
                {isSmartRemovalEnabled() && (
                  <Route exact strict path="/remove/instant/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
                )}
                {isSmartRemovalEnabled() && (
                  <Route exact strict path="/remove/smart/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
                )}
                {isSmartRemovalEnabled() && (
                  <Route
                    exact
                    strict
                    path="/remove/smart/retry/:currencyIdA/:currencyIdB"
                    component={RemoveLiquidity}
                  />
                )}
                {isSmartRemovalEnabled() && (
                  <Route exact strict path="/remove/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />
                )}
                <Route exact path="/zap" component={FlashLp} />
                <Route exact path="/zap/:currencyIdA" component={FlashLp} />
                <Route exact path="/zap/:currencyIdA/:lpPairIdB" component={FlashLp} />
                <Route exact strict path="/tradingBattle" component={TradingBattle} />
                <Route path={Path.XVVS} component={xVvs} />
                {/* Redirect */}
                <Route path="/pool">
                  <Redirect to="/liquidity" />
                </Route>
                <Route path="/staking">
                  <Redirect to="/mines" />
                </Route>
                <Route path="/syrup">
                  <Redirect to="/mines" />
                </Route>

                {/* 404 */}
                <Route component={NotFound} />
              </Switch>
            </SuspenseWithChunkError>
          </LayoutWithMenu>
          <ToastListener />
          <DatePickerPortal />
        </ScrollToTop>
      </Router>
    </ErrorBoundary>
  )
}

const AppWithProvider = () => {
  return (
    <RootProviders>
      {/* Updater */}
      <ListsUpdater />
      <TransactionUpdater />
      <MulticallUpdater />
      <App />
    </RootProviders>
  )
}

export default React.memo(AppWithProvider)
